import React, { useEffect, useState } from 'react';
import { Phone, Calendar } from 'lucide-react';
import { contentAPI } from '../utils/api';
import { track } from '../utils/analytics';

// Module-level cache so this bar never refetches / flashes during the session.
let CACHED_INFO = null;
let CACHED_NAV = null;
let CACHED_PROMISE = null;

/**
 * Persistent mobile-only sticky bottom bar with Call + Reserve actions.
 * Hidden on md+ screens where the desktop Navbar already pins the CTA top-right.
 */
const MobileBottomBar = () => {
  const [info, setInfo] = useState(CACHED_INFO);
  const [nav, setNav] = useState(CACHED_NAV);

  useEffect(() => {
    if (CACHED_INFO && CACHED_NAV) return;
    if (!CACHED_PROMISE) {
      CACHED_PROMISE = Promise.all([
        contentAPI.getRestaurantInfo().catch(() => ({})),
        contentAPI.getNav().catch(() => ({})),
      ]);
    }
    CACHED_PROMISE.then(([i, n]) => {
      CACHED_INFO = i || {};
      CACHED_NAV = n || {};
      setInfo(CACHED_INFO);
      setNav(CACHED_NAV);
    });
  }, []);

  const phone = info?.phone;
  const reserveLink = nav?.cta_link;
  const reserveLabel = nav?.cta_label || 'Reserve';

  // Render nothing until we have at least one actionable link.
  if (!phone && !reserveLink) return null;

  // Strip non-digits for tel: href so any human-readable format works.
  const telHref = phone ? `tel:${phone.replace(/[^+\d]/g, '')}` : null;

  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-black text-white border-t border-white/10 shadow-[0_-4px_20px_rgba(0,0,0,0.35)]"
      data-testid="mobile-bottom-bar"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex">
        {telHref && (
          <a
            href={telHref}
            onClick={() => track('Contact', { content_name: 'Phone Call', location: 'mobile-bar' })}
            className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold tracking-wide uppercase text-white hover:bg-white/5 active:bg-white/10 transition-colors"
            data-testid="mobile-bar-call"
          >
            <Phone size={16} /> Call
          </a>
        )}
        {telHref && reserveLink && <div className="w-px bg-white/10" />}
        {reserveLink && (
          <a
            href={reserveLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('Schedule', { content_name: 'Reservation Click', location: 'mobile-bar' })}
            className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold tracking-wide uppercase bg-[#C9A86C] text-black hover:bg-[#B8956A] active:bg-[#A88758] transition-colors"
            data-testid="mobile-bar-reserve"
          >
            <Calendar size={16} /> {reserveLabel.length > 14 ? 'Reserve' : reserveLabel}
          </a>
        )}
      </div>
    </div>
  );
};

export default MobileBottomBar;
